"use client"

import { useState } from "react"
import {
  Stack,
  TextInput,
  PasswordInput,
  Button,
  Text
} from "@mantine/core"
import {
  TbUser,
  TbLock,
  TbLogin,
  TbCheck
} from "react-icons/tb"
import { notify } from "../../components/NotificationWrapper"

export interface LoginFormTranslations {
  email: string
  password: string
  enterEmail: string
  enterPassword: string
  signIn: string
  signingIn: string
  loginSuccessful: string
  redirecting: string
  fillTestCredentials: string
  invalidCredentials: string
}

export interface TestCredentials {
  email: string
  password: string
  label?: string
}

export interface LoginFormProps {
  onLogin: (email: string, password: string) => Promise<void>
  onSuccess?: () => void
  onError?: (error: string) => void
  isLoading?: boolean
  showTestCredentials?: boolean
  testCredentials?: TestCredentials[]
  translations?: Partial<LoginFormTranslations>
  className?: string
}

// Default English translations as fallback
const defaultTranslations: LoginFormTranslations = {
  email: "Email",
  password: "Password",
  enterEmail: "Enter your email",
  enterPassword: "Enter your password",
  signIn: "Sign In",
  signingIn: "Signing in...",
  loginSuccessful: "Login Successful!",
  redirecting: "Redirecting...",
  fillTestCredentials: "Fill Test Credentials",
  invalidCredentials: "Invalid credentials. Please try again."
}

export function LoginForm({
  onLogin,
  onSuccess,
  onError,
  isLoading = false,
  showTestCredentials = false,
  testCredentials = [],
  translations = {},
  className = ""
}: LoginFormProps) {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)

  // Merge with default translations
  const t = { ...defaultTranslations, ...translations }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setSuccess(false)
    setLoading(true)

    try {
      await onLogin(email, password)
      setSuccess(true)
      notify.success(t.loginSuccessful)
      onSuccess?.()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t.invalidCredentials
      setError(errorMessage)
      notify.error(errorMessage)
      onError?.(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const fillCredentials = (credentials: TestCredentials) => {
    setEmail(credentials.email)
    setPassword(credentials.password)
  }

  const currentLoading = isLoading || loading

  if (success) {
    return (
      <Stack gap="md" align="center" className={className}>
        <div className="text-center">
          <TbCheck size={48} className="text-green-500 mx-auto mb-3" />
          <Text size="lg" fw={600} className="text-green-600 dark:text-green-400">
            {t.loginSuccessful}
          </Text>
          <Text size="sm" className="text-gray-600 dark:text-gray-400 mt-2">
            {t.redirecting}
          </Text>
        </div>
      </Stack>
    )
  }

  return (
    <form onSubmit={handleSubmit} className={className}>
      <Stack gap="md">
        <TextInput
          label={t.email}
          placeholder={t.enterEmail}
          leftSection={<TbUser size={16} />}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          size="md"
          disabled={currentLoading}
        />

        <PasswordInput
          label={t.password}
          placeholder={t.enterPassword}
          leftSection={<TbLock size={16} />}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          size="md"
          disabled={currentLoading}
        />

        <Button
          type="submit"
          leftSection={<TbLogin size={16} />}
          loading={currentLoading}
          size="md"
          fullWidth
          className="mt-2"
        >
          {currentLoading ? t.signingIn : t.signIn}
        </Button>

        {showTestCredentials && testCredentials.length > 0 && (
          <Stack gap="xs">
            {testCredentials.map((cred, index) => (
              <Button
                key={index}
                type="button"
                variant="light"
                onClick={() => fillCredentials(cred)}
                size="sm"
                fullWidth
                disabled={currentLoading}
              >
                {cred.label || `${t.fillTestCredentials} ${index + 1}`}
              </Button>
            ))}
          </Stack>
        )}
      </Stack>
    </form>
  )
}
